/**
 * Debounced auto-enrich for Todos table Spec + Activity columns.
 * Reuses POST /api/workspec-status and /api/last-comments-batch (same as per-row refresh).
 */

import { useEffect, useRef, useCallback, type MutableRefObject, type Dispatch, type SetStateAction } from 'react';
import { fetchWithAuth, isOfflineOrUnreachableFetchError } from '@/lib/api';
import type { BasecampAssignment } from '@/types/basecamp';

const ENRICH_CHUNK_SIZE = 15;
const MAX_ENRICH_CHUNKS = 4;
const CHUNK_PAUSE_MS = 400;
const DEBOUNCE_MS = 450;
const WORKSPEC_TTL_MS = 60 * 60 * 1000;
const COMMENT_TTL_MS = 48 * 60 * 60 * 1000;
/** Back off per-todo after network/CORS failures so a down API does not spam requests. */
const ENRICH_FAIL_COOLDOWN_MS = 60_000;

export type WorkspecMapEntry = { status: string; filePath: string };
export type LastCommentMapEntry = {
  text: string;
  createdAt: string;
  creatorName?: string;
  stale?: boolean;
};

function parseTimestamp(raw?: string | null): number {
  if (!raw) return 0;
  const ms = new Date(raw).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

export function needsWorkspecEnrich(a: BasecampAssignment): boolean {
  const enrichedAt = parseTimestamp(a._workspec_enriched_at);
  if (!enrichedAt) return true;
  if (Date.now() - enrichedAt > WORKSPEC_TTL_MS) return true;
  const todoUpdated = a._updated_at?.includes('T') ? parseTimestamp(a._updated_at) : 0;
  return todoUpdated > enrichedAt;
}

export function needsActivityEnrich(a: BasecampAssignment): boolean {
  const cachedAt = parseTimestamp(a._last_comment_cached_at);
  if (!cachedAt) return true;
  return Date.now() - cachedAt > COMMENT_TTL_MS;
}

export function hydrateMapsFromAssignments(assignments: BasecampAssignment[]): {
  workspecMap: Record<string, WorkspecMapEntry>;
  lastCommentMap: Record<string, LastCommentMapEntry>;
} {
  const staleMs = COMMENT_TTL_MS;
  const now = Date.now();
  const workspecMap: Record<string, WorkspecMapEntry> = {};
  const lastCommentMap: Record<string, LastCommentMapEntry> = {};

  for (const a of assignments) {
    const id = String(a.id);
    if (a._workspec_enriched_at != null || a._workspec_status != null) {
      workspecMap[id] = {
        status: a._workspec_status ?? 'none',
        filePath: a._workspec_file_path ?? '',
      };
    }
    if (a._last_comment_text) {
      const cachedAtMs = parseTimestamp(a._last_comment_cached_at);
      lastCommentMap[id] = {
        text: (a._last_comment_text || '').replace(/\s+/g, ' ').slice(0, 120),
        createdAt: a._last_comment_at ?? '',
        creatorName: a._last_comment_creator ?? undefined,
        stale: cachedAtMs > 0 && now - cachedAtMs > staleMs,
      };
    }
  }

  return { workspecMap, lastCommentMap };
}

type MergeFns = {
  mergeWorkspec: (todoId: string, entry: { status?: string; filePath?: string | null }) => void;
  mergeComment: (
    todoId: string,
    entry: { content?: string; created_at?: string; creator_name?: string } | null
  ) => void;
};

async function enrichChunk(
  chunk: BasecampAssignment[],
  { mergeWorkspec, mergeComment }: MergeFns
): Promise<boolean> {
  const items = chunk.map((a) => ({
    id: String(a.id),
    projectId: a.project?.id ?? '',
    updatedAt: a._updated_at ?? '',
  }));

  let response: Response;
  try {
    response = await fetchWithAuth('/api/todos-enrich-batch', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items }),
    });
  } catch (err) {
    if (!isOfflineOrUnreachableFetchError(err)) {
      console.warn('[todos-column-enrich] batch request failed:', err);
    }
    return false;
  }

  if (!response.ok) return false;
  const data = (await response.json()) as {
    workspec?: Record<string, { status?: string; filePath?: string | null }>;
    comments?: Record<string, { content?: string; created_at?: string; creator_name?: string } | null>;
  };

  for (const [todoId, entry] of Object.entries(data.workspec ?? {})) {
    if (entry?.status != null) mergeWorkspec(todoId, entry);
  }
  for (const [todoId, entry] of Object.entries(data.comments ?? {})) {
    mergeComment(
      todoId,
      entry as { content?: string; created_at?: string; creator_name?: string } | null
    );
  }
  return true;
}

export function useTodosColumnEnrich(
  assignments: BasecampAssignment[] | undefined,
  filteredTodos: BasecampAssignment[],
  isLoading: boolean,
  mergeWorkspec: (todoId: string, entry: { status?: string; filePath?: string | null }) => void,
  mergeComment: (
    todoId: string,
    entry: { content?: string; created_at?: string; creator_name?: string } | null
  ) => void,
  enrichingIdsRef: MutableRefObject<Set<string>>,
  setEnrichingIds: Dispatch<SetStateAction<Set<string>>>
) {
  const enrichRunRef = useRef(0);
  const filteredTodosRef = useRef(filteredTodos);
  filteredTodosRef.current = filteredTodos;
  const listKey = filteredTodos.map((a) => String(a.id)).join(',');
  /** Todos enriched this mount; assignment rows are not mutated so needsWorkspecEnrich stays true. */
  const sessionEnrichedRef = useRef<Set<string>>(new Set());
  const failCooldownRef = useRef<Map<string, number>>(new Map());

  const syncEnrichingIds = useCallback(() => {
    setEnrichingIds(new Set(enrichingIdsRef.current));
  }, [enrichingIdsRef, setEnrichingIds]);

  const runEnrichForChunk = useCallback(
    async (chunk: BasecampAssignment[]) => {
      const ids = chunk.map((a) => String(a.id));
      for (const id of ids) enrichingIdsRef.current.add(id);
      syncEnrichingIds();
      try {
        const ok = await enrichChunk(chunk, {
          mergeWorkspec,
          mergeComment: (todoId, entry) => {
            if (entry && entry.content != null) {
              mergeComment(todoId, entry);
            } else {
              mergeComment(todoId, null);
            }
          },
        });
        const now = Date.now();
        for (const id of ids) {
          if (ok) {
            sessionEnrichedRef.current.add(id);
            failCooldownRef.current.delete(id);
          } else {
            failCooldownRef.current.set(id, now);
          }
        }
      } finally {
        for (const id of ids) enrichingIdsRef.current.delete(id);
        syncEnrichingIds();
      }
    },
    [enrichingIdsRef, mergeWorkspec, mergeComment, syncEnrichingIds]
  );

  useEffect(() => {
    if (isLoading || !assignments?.length || filteredTodosRef.current.length === 0) return;

    const runId = ++enrichRunRef.current;

    const timer = setTimeout(async () => {
      const visible = filteredTodosRef.current;
      const now = Date.now();
      const candidates = visible.filter((a) => {
        const id = String(a.id);
        if (enrichingIdsRef.current.has(id)) return false;
        if (sessionEnrichedRef.current.has(id)) return false;
        const failedAt = failCooldownRef.current.get(id);
        if (failedAt != null && now - failedAt < ENRICH_FAIL_COOLDOWN_MS) return false;
        return needsWorkspecEnrich(a) || needsActivityEnrich(a);
      });

      if (candidates.length === 0) return;

      const maxTodos = ENRICH_CHUNK_SIZE * MAX_ENRICH_CHUNKS;
      const toProcess = candidates.slice(0, maxTodos);

      for (let i = 0; i < toProcess.length; i += ENRICH_CHUNK_SIZE) {
        if (enrichRunRef.current !== runId) return;
        const chunk = toProcess.slice(i, i + ENRICH_CHUNK_SIZE);
        await runEnrichForChunk(chunk);
        if (i + ENRICH_CHUNK_SIZE < toProcess.length) {
          await new Promise((r) => setTimeout(r, CHUNK_PAUSE_MS));
        }
      }
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [isLoading, assignments?.length, listKey, runEnrichForChunk, enrichingIdsRef]);

  useEffect(() => {
    const visibleIds = new Set(filteredTodos.map((a) => String(a.id)));
    for (const id of sessionEnrichedRef.current) {
      if (!visibleIds.has(id)) sessionEnrichedRef.current.delete(id);
    }
    for (const id of failCooldownRef.current.keys()) {
      if (!visibleIds.has(id)) failCooldownRef.current.delete(id);
    }
  }, [listKey, filteredTodos]);
}

/** Merge helpers for React state setters */
export function applyWorkspecMerge(
  prev: Record<string, WorkspecMapEntry>,
  todoId: string,
  entry: { status?: string; filePath?: string | null }
): Record<string, WorkspecMapEntry> {
  if (entry?.status == null) return prev;
  return { ...prev, [todoId]: { status: entry.status, filePath: entry.filePath ?? '' } };
}

export function applyCommentMerge(
  prev: Record<string, LastCommentMapEntry>,
  todoId: string,
  entry: { content?: string; created_at?: string; creator_name?: string } | null
): Record<string, LastCommentMapEntry> {
  if (!entry || entry.content == null) return prev;
  return {
    ...prev,
    [todoId]: {
      text: (entry.content || '').replace(/\s+/g, ' ').slice(0, 120),
      createdAt: entry.created_at ?? '',
      creatorName: entry.creator_name,
      stale: false,
    },
  };
}
